import { ChatMessages } from "@storytell/cp-client";
import type { createStreamingChat } from "@storytell/streaming";
import { findOperationById } from "@storytell/streaming";
import { Show, createMemo } from "solid-js";

interface InterstitialGeneratingViewerProps {
  operationId: string;
  blockId?: string; // Optional block ID for block-specific content
  chat: ReturnType<typeof createStreamingChat>;
  title?: string;
}

/**
 * Renders generating/data content in the extended panel using pre/code blocks
 * Designed for structured data, logs, or raw output
 */
export const InterstitialGeneratingViewer = (props: InterstitialGeneratingViewerProps) => {
  // Find all data/generating blocks for this operation, or specific block if blockId provided
  const dataBlocks = createMemo(() => {
    const conversation = props.chat.chatState.conversation;
    const operation = findOperationById(conversation, props.operationId);

    if (!operation) return [];

    let blocks = operation.blocks.filter(
      (block) =>
        block.messageType === ChatMessages.MessageType.INTERSTITIAL_DATA ||
        (block.messageType === ChatMessages.MessageType.CONTENT && !block.isComplete), // Generating content
      // TODO: @andi no more text on blocks, figure out if this still needed
      // (block.messageType === undefined && block.text && block.text.trim()), // Blocks without messageType but with content
    );

    // If blockId is provided, filter to only that specific block
    if (props.blockId) {
      blocks = blocks.filter((block) => block.id === props.blockId);
    }

    return blocks;
  });

  // Combine all content from data blocks
  const combinedContent = createMemo(() => {
    const blocks = dataBlocks();
    if (blocks.length === 0) return "";

    return (
      blocks
        // TODO: @andi no more text on blocks, figure out if this still needed
        // .map((block) => block.text || "")
        .map((block) => "")
        .filter(Boolean)
        .join("\n\n")
    );
  });

  const isAnyBlockProcessing = createMemo(() => {
    return dataBlocks().some((block) => !block.isComplete);
  });

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(combinedContent());
    } catch (err) {
      // Silently handle clipboard errors
    }
  };

  return (
    <div data-renderer="interstitial-generating-viewer" class="h-full flex flex-col">
      {/* Header */}
      <div class="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span class="text-lg">📊</span>
            <div>
              <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{props.title || "Generated Data"}</h2>
              <p class="text-sm text-gray-600 dark:text-gray-400">Raw output and structured data</p>
            </div>
            <Show when={isAnyBlockProcessing()}>
              <div class="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full flex-shrink-0" />
            </Show>
          </div>

          <Show when={combinedContent()}>
            <button
              type="button"
              onClick={copyToClipboard}
              class="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300 transition-colors"
              title="Copy all content"
            >
              Copy
            </button>
          </Show>
        </div>
      </div>

      {/* Content */}
      <div class="flex-1 overflow-auto">
        <Show
          when={combinedContent()}
          fallback={
            <div class="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div class="text-center">
                <div class="text-lg mb-2">📊</div>
                <div class="text-sm">No data content yet</div>
                <Show when={isAnyBlockProcessing()}>
                  <div class="text-xs mt-1 opacity-60">Generating...</div>
                </Show>
              </div>
            </div>
          }
        >
          <div class="relative">
            <pre class="p-6 text-sm font-mono bg-gray-900 dark:bg-black text-gray-100 dark:text-gray-200 overflow-x-auto">
              <code>{combinedContent()}</code>
            </pre>
          </div>
        </Show>
      </div>

      {/* Footer with block count and stats */}
      <div class="flex-shrink-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>
            {dataBlocks().length} data block{dataBlocks().length !== 1 ? "s" : ""}
          </span>
          <Show when={combinedContent()}>
            <span>
              {combinedContent().length} characters, {combinedContent().split("\n").length} lines
            </span>
          </Show>
        </div>
      </div>
    </div>
  );
};
